/* eslint-disable import-extensions/file-extension-required */
import { graphql } from 'react-relay';
import set from 'lodash/set';
import uniqBy from 'lodash/uniqBy';
import type { NetworkStatus } from 'apollo-client';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type { LoadingState } from '../homeViewsQueryHelpers.ts';
import { getLoadingState, notEmpty, byTitle } from '../homeViewsQueryHelpers.ts';
import { transformDraftToPageCard } from '../pageCardTransformers.ts';

import type {
	useDraftsDataHomeDraftsQuery$data,
	useDraftsDataHomeDraftsQuery$variables,
} from './__generated__/useDraftsDataHomeDraftsQuery.graphql';
import { DRAFTS_QUERY_OPTIONS } from './draftsQueryHelpers';

const UseDraftsDataHomeDraftsQuery = graphql`
	query useDraftsDataHomeDraftsQuery($cqlcontext: String, $first: Int, $after: String) {
		search: confluence_search(
			cql: "type in (page,blogpost) order by lastModified desc"
			cqlcontext: $cqlcontext
			first: $first
			after: $after
		) {
			nodes {
				content {
					id
					title
					type
					subType
					links {
						webui
						editui
					}
					excerpt(length: 372)
					metadata {
						currentuser {
							favourited {
								isFavourite
							}
						}
						comments {
							commentsCount
						}
					}
					space {
						alias
						id
						key
						name
					}
					likes {
						count
					}
					status
					blank
					metadata {
						lastModifiedDate
					}
					editorVersion: properties(key: "editor") {
						nodes {
							id
							key
							value
						}
					}
					emoji: properties(keys: ["emoji-title-draft"]) {
						nodes {
							id
							key
							value
						}
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

type UseDraftsData = ({
	filterText,
	translatedUntitled,
}: {
	filterText: string;
	translatedUntitled: string;
}) => {
	pageCardData: GlobalPageCard[];
	loading: LoadingState;
	networkStatus: NetworkStatus;
	hasNextPage: boolean;
	loadMore: () => void;
	error?: ApolloErrorInterop;
};

const isExpectedError = (error): boolean =>
	error.message.includes(
		"com.atlassian.confluence.api.service.exceptions.api.BadRequestException: CQL was parsed but the search manager was unable to execute the search. CQL: 'type in (page,blogpost) order by lastModified desc'",
	);

export const useDraftsData: UseDraftsData = ({ filterText, translatedUntitled }) => {
	const { data, loading, networkStatus, error, fetchMore } = useQueryInterop<
		useDraftsDataHomeDraftsQuery$data,
		useDraftsDataHomeDraftsQuery$variables
	>(UseDraftsDataHomeDraftsQuery, {
		...DRAFTS_QUERY_OPTIONS,
		notifyOnNetworkStatusChange: true,
	});
	if (error) {
		if (isExpectedError(error)) {
			markErrorAsHandled(error);
		}
	}
	const pageCardData = uniqBy(
		(data?.search?.nodes || [])
			.filter(notEmpty)
			.filter((node) => notEmpty(node.content))
			.map((node) => transformDraftToPageCard(node, translatedUntitled))
			.filter((node) => byTitle(filterText, node.title.text)),
		(pageCard) => pageCard.id,
	);
	const hasNextPage = data?.search?.pageInfo?.hasNextPage || false;
	const endCursor = data?.search?.pageInfo?.endCursor || '';
	const loadMore = () => {
		if (hasNextPage && endCursor && !loading) {
			void fetchMore({
				variables: {
					after: endCursor,
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prev;
					}
					const result = { ...fetchMoreResult };
					const prevNodes = prev.search?.nodes || [];
					const fetchMoreNodes = fetchMoreResult.search?.nodes || [];
					const mergedNodes = [...prevNodes, ...fetchMoreNodes];
					set(result, 'search.nodes', mergedNodes);
					return result;
				},
			});
		}
	};
	return {
		pageCardData,
		loading: getLoadingState(networkStatus),
		networkStatus,
		error,
		hasNextPage,
		loadMore,
	};
};
